import { Box3, Object3D, Quaternion, Ray, Vector3, Group, Line3, Mesh, BoxGeometry, MeshBasicMaterial } from 'three';
import { IV3D } from '@/Interfaces/v3d';
import { rayIntersection, sides, wardrobeObjectType, xyz } from '@/Interfaces/common';
import { RaycastBox } from '@/Engine/RaycastBox';
import { getStartPointMesh } from '@/Engine/startPointMesh';
import { SelectionBox, selectionBoxTarget } from '@/Engine/SelectionBox';
import { engineConfig, rayZShift, startPointPointer } from '@/Constants/3dConfig';
import { $ } from '@/Engine/stateInstance';
import { OBB } from 'three/examples/jsm/math/OBB';

declare let v3d: IV3D;

import { InstancingManager } from './InstancingManager';

export interface objectProps {
    uuid: string;
    width: number;
    height: number;
    depth: number;
    raycastBox?: boolean;
    type: wardrobeObjectType;
    compatibilityWidth?: string;
    compatibilityDepth?: string;
    color: string;
    side: sides;
    // onChangePosition?: (uuid: string, position: xyz)=>void;
}
export class WardrobeObject {
    uuid: string;
    public model: Object3D;
    public decorModel: Object3D;
    private _width: number;
    public _pointer: Object3D;
    public selectionBox: SelectionBox | null = null;
    public box: Box3 = new Box3();
    public worldBox: Box3 = new Box3();
    public obb: OBB = new OBB();
    public childPosition: xyz = [ 0,0,0 ];
    public type: wardrobeObjectType = '';
    public rays = {
        up: new Ray(),
        down: new Ray(),
        left: new Ray(),
        right: new Ray(),
    };
    compatibilityWidth?: string;
    compatibilityDepth?: string;
    color: string;
    public side: sides;
    rayCastBoxEnable = false;

    // onChangePosition?: (uuid: string, position: xyz)=>void;
    set width(width: number) {
        this._width = width;
        this.raycastBox.updateGeometry(this._width, this._height, this._depth);
    }
    get width() {
        return this._width;
    }

    private _height: number;
    set height(height: number) {
        this._height = height;
        this.raycastBox.updateGeometry(this._width, this._height, this._depth);
    }
    get height() {
        return this._height;
    }

    private _depth: number;
    set depth(depth: number) {
        this._depth = depth;
        this.raycastBox.updateGeometry(this._width, this._height, this._depth);
    }
    get depth() {
        return this._depth;
    }

    private _startPoint: xyz = [ 0,0,0 ];
    set startPoint(pos: xyz) {
        // this.selectionBox.select(this);
        this._startPoint = pos;
        this.raycastBox.model.position.set(...pos);
    }
    get startPoint() {
        return this._startPoint;
    }

    set position(position: Vector3) {
        if (position.distanceTo(this.model.position) < 0.001) return;
        this.model.position.copy(position);
        this._updateBox();
        InstancingManager.getInstance().setNeedsUpdate();
        // if (this.onChangePosition)
        //     this.onChangePosition(this.uuid, [ position.x, position.y, position.z ]);
    }

    get position() {
        return this.model.position.clone();
    }

    get quaternion() {
        const quaternion = new Quaternion();
        this.model.getWorldQuaternion(quaternion);
        return quaternion;
    }

    rotate(point: Vector3) {
        return point.clone().applyQuaternion(this.quaternion);
    }

    public startPointGlobal: xyz;

    public raycastBox: RaycastBox;
    constructor({ uuid, width, height, depth, raycastBox = false, type = '', compatibilityWidth,
                    compatibilityDepth, color, side }: objectProps) {
        this.compatibilityWidth = compatibilityWidth;
        this.compatibilityDepth = compatibilityDepth;
        this.uuid = uuid;
        // this.onChangePosition = onChangePosition;
        this.model = new Group();
        this.decorModel = new Group();
        this.model.add(this.decorModel);
        this._pointer = getStartPointMesh();
        this._pointer.visible = startPointPointer;
        this.model.add(this._pointer);
        this.model.userData.parent = this;
        this._width = width;
        this._height = height;
        this._depth = depth;
        this.startPointGlobal = [ 0,0,0 ];

        this.raycastBox = new RaycastBox(this.width, this.height, this.depth, this);
        this.type = type;
        this.side  = side;
        // this.model.add(new ArrowHelper(this.rays.up.direction, this.rays.up.origin, 100, Math.random() * 0xffffff));
        this.rayCastBoxEnable = raycastBox;
        if (this.rayCastBoxEnable) {
            this.model.add(this.raycastBox.model);
        }
        this.color = color;
        this._updateBox();
    }

    select() {
        this.unselect();
        this.selectionBox = $.selectionBox;
        this.selectionBox.select(this as unknown as selectionBoxTarget);
        // this.model.add(selectionBox.model);
    }

    unselect() {
        if (!this.selectionBox) return;
        this.selectionBox.unselect();
        this.selectionBox = null;
    }

    _updateBox() {
        this.box.makeEmpty();
        if (this.rayCastBoxEnable) {
            this.box.expandByObject(this.raycastBox.model);
        } else {
            // this.decorModel.removeFromParent();
            this.box.expandByObject(this.model);
            // this.model.add(this.decorModel);
        }
    }

    clearDecor() {
        this.decorModel.clear();
    }

    getLowerObject(ray: Ray, objects: Array<WardrobeObject>) {
        const out: rayIntersection = {
            distance: null,
            object: null,
            point: null,
        };

        objects.forEach((child)=> {
            if (child.uuid === this.uuid) return null;
            const point = new Vector3();
            const intersect = child.obb.intersectRay(ray, point);
            if (!intersect) return null;
            const line =  new Line3(ray.origin, intersect);
            if (!out.distance || out.distance > line.distance()) {
                out.distance = line.distance();
                out.object = child;
                out.point = intersect;
            }
        });

        return out;
    }

    getLowerDistance(ray: Ray, objects: Array<WardrobeObject>): number {
        const { distance } = this.getLowerObject(ray, objects);
        // Добавить пересечение с потолком
        return distance ?? 3;
    }

    get localSize() {
        const worldQuaternion = new Quaternion();
        this.model.getWorldQuaternion(worldQuaternion);
        const sizes = new Vector3(this.width, this.height, this.depth);
        // const sizes = new Vector3();
        // this.box.getSize(sizes);
        // sizes.applyQuaternion(worldQuaternion.invert());
        // sizes.x = Math.abs(sizes.x);
        // sizes.y = Math.abs(sizes.y);
        // sizes.z = Math.abs(sizes.z);
        return sizes;
    }

    computeObb(_shift?: Vector3, _rayZShift?: number) {
        this._updateBox();
        const worldPos = new Vector3();
        const worldQuaternion = new Quaternion();
        this.model.getWorldQuaternion(worldQuaternion);
        const sizes = new Vector3();
        this.box.getSize(sizes);
        sizes.applyQuaternion(worldQuaternion);
        const shift = _shift ?? new Vector3(0,-Math.abs(sizes.y/2), Math.abs(sizes.z/2)).applyQuaternion(worldQuaternion);
        // const halfSize = new Vector3(this.width/2,this.height/2, this.depth/2).applyQuaternion(worldQuaternion);
        // const size = new Vector3(this.width,this.height, this.depth).applyQuaternion(worldQuaternion);
        // const box = new Box3();
        this.model.getWorldPosition(worldPos);
        // this.box.clone().applyMatrix4(this.model.matrixWorld);
        const obbCenter = new Vector3(
            worldPos.x  + shift.x,
            worldPos.y + shift.y,
            worldPos.z + shift.z);
        // this.obb.set(obbCenter, halfSize, new Matrix3().setFromMatrix4(this.model.matrixWorld));
        this.obb.fromBox3(this.box.clone());
        // const size = new Vector3();
        // this.box.getSize(size);
        // console.log(size);
        // this.obb.halfSize.copy(halfSize);
        this.obb.center.copy(obbCenter);
        // this.obb.halfSize.copy(halfSize);

        // Костыль, который добавляет учет стоек
        let xShift = 0;
        if ([ 'rackModule', 'shelf', 'panel' ].includes(this.type)) {
            xShift = 0.012;
        }

        const rayUpPoint = new Vector3();
        const rayDownPoint = new Vector3();
        const rayLeftPoint = new Vector3();
        const rayRightPoint = new Vector3();

        const rayOrigin = new Vector3(0,0,_rayZShift ?? rayZShift)
            .applyQuaternion(worldQuaternion)
            .add(worldPos);

        let yShift = 0;
        if (this.type === 'door')
            yShift = this.height;
        if (this.type === 'window')
            yShift = this.height/2;
        // const yShift = [ 'window' ].includes(this.type??'')?this.height/2:0;

        rayUpPoint.copy(rayOrigin);

        rayUpPoint.set(
            rayOrigin.x,
            rayOrigin.y + yShift,
            rayOrigin.z,
        );

        rayDownPoint.set(
            rayOrigin.x,
            rayOrigin.y - this.localSize.y + yShift,
            rayOrigin.z,
        );
        rayLeftPoint.set(
            -this.localSize.x/2 - xShift,
            -this.localSize.y/2 + yShift,
            0)
            .applyQuaternion(worldQuaternion)
            .add(rayOrigin);

        rayRightPoint.set(
            this.localSize.x/2 + xShift,
            -this.localSize.y/2 + yShift,
            0)
            .applyQuaternion(worldQuaternion)
            .add(rayOrigin);

        // rayUpPoint.set(
        //     this.obb.center.x  + this.obb.halfSize.x,
        //     this.obb.center.y  + this.obb.halfSize.y,
        //     this.obb.center.z  + this.obb.halfSize.z,
        // );
        this.rays.up.set(rayUpPoint, new Vector3(0,1,0));
        this.rays.down.set(rayDownPoint, new Vector3(0,-1,0));
        this.rays.left.set(rayLeftPoint, new Vector3(-1,0,0).applyQuaternion(worldQuaternion));
        this.rays.right.set(rayRightPoint, new Vector3(1,0,0).applyQuaternion(worldQuaternion));

        if (engineConfig.boxHelper) {
            $.app.scene.children.forEach((child)=>{
                if (child.name === 'temp' + this.uuid) $.app.scene.remove(child);
            });
            // const helper = new Box3Helper(this.obb., new Color(0xffff00));
            const helper = new Mesh(new BoxGeometry(1,1,1,1,1,1), new MeshBasicMaterial({ wireframe: true }));
            const size = new Vector3();
            this.obb.getSize(size);
            helper.scale.copy(size);
            helper.position.copy(this.obb.center);
            helper.name = 'temp'+this.uuid;
            // helper.position.copy(worlPos);
            $.app.scene.add(helper);
        }
    }

    destroy() {
        if (engineConfig.boxHelper) {
            $.app.scene.children.forEach((child) => {
                if (child.name === 'temp' + this.uuid) $.app.scene.remove(child);
            });
        }
        this.model.clear();
        this.model.removeFromParent();
    }
}
